import { FC, useState } from "react"
import { Check, ClipboardList } from "lucide-react"
import {
  StaffFormField,
  StaffFormRequest
} from "../../hooks/use-staff-chat-stream"

interface Props {
  form: StaffFormRequest
  disabled: boolean
  onSubmit: (form: StaffFormRequest, values: Record<string, unknown>) => void
}

type FormValues = Record<string, string | boolean | string[]>

const INPUT_CLASSES =
  "w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:bg-gray-50 disabled:text-gray-400"

function initialValues(fields: StaffFormField[]): FormValues {
  const values: FormValues = {}
  for (const field of fields) {
    if (field.type === "checkbox") {
      values[field.name] = field.defaultValue === true
    } else if (field.type === "multiselect") {
      values[field.name] = Array.isArray(field.defaultValue)
        ? field.defaultValue.map(String)
        : []
    } else {
      values[field.name] =
        field.defaultValue !== undefined && field.defaultValue !== null
          ? String(field.defaultValue)
          : ""
    }
  }
  return values
}

function isEmpty(field: StaffFormField, value: FormValues[string]): boolean {
  if (field.type === "checkbox") return false
  if (field.type === "multiselect") return (value as string[]).length === 0
  return String(value).trim() === ""
}

/** Coerce UI state into the typed JSON object sent back to the agent. */
function coerceValues(
  fields: StaffFormField[],
  values: FormValues
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const field of fields) {
    const raw = values[field.name]
    if (field.type === "checkbox") out[field.name] = raw === true
    else if (field.type === "multiselect") out[field.name] = raw
    else if (field.type === "number") {
      const trimmed = String(raw).trim()
      out[field.name] = trimmed === "" ? null : Number(trimmed)
    } else {
      const trimmed = String(raw).trim()
      out[field.name] = trimmed === "" ? null : trimmed
    }
  }
  return out
}

const FieldInput: FC<{
  field: StaffFormField
  value: FormValues[string]
  disabled: boolean
  onChange: (value: FormValues[string]) => void
}> = ({ field, value, disabled, onChange }) => {
  switch (field.type) {
    case "textarea":
      return (
        <textarea
          value={value as string}
          disabled={disabled}
          placeholder={field.placeholder}
          onChange={e => onChange(e.target.value)}
          rows={3}
          className={INPUT_CLASSES}
        />
      )
    case "select":
      return (
        <select
          value={value as string}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
          className={INPUT_CLASSES}
        >
          <option value="">{field.placeholder || "Select…"}</option>
          {field.options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )
    case "multiselect":
      return (
        <div className="space-y-1">
          {field.options?.map(option => {
            const selected = (value as string[]).includes(option.value)
            return (
              <label
                key={option.value}
                className="flex items-center gap-2 text-sm text-neutral-700"
              >
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={disabled}
                  onChange={() =>
                    onChange(
                      selected
                        ? (value as string[]).filter(v => v !== option.value)
                        : [...(value as string[]), option.value]
                    )
                  }
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                {option.label}
              </label>
            )
          })}
        </div>
      )
    case "checkbox":
      return (
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            onChange={e => onChange(e.target.checked)}
            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          {field.placeholder || field.label}
        </label>
      )
    default:
      return (
        <input
          type={field.type}
          value={value as string}
          disabled={disabled}
          placeholder={field.placeholder}
          onChange={e => onChange(e.target.value)}
          className={INPUT_CLASSES}
        />
      )
  }
}

/**
 * Inline form requested by the agent (requestFormInput tool). Submission is
 * sent back into the conversation as a JSON user message shaped by the
 * form's field names.
 */
const FormCard: FC<Props> = ({ form, disabled, onSubmit }) => {
  const [values, setValues] = useState<FormValues>(() =>
    initialValues(form.fields)
  )
  const [showErrors, setShowErrors] = useState(false)

  const locked = disabled || form.submitted === true
  const missing = form.fields.filter(
    f => f.required && isEmpty(f, values[f.name])
  )

  const submit = () => {
    if (locked) return
    if (missing.length > 0) {
      setShowErrors(true)
      return
    }
    onSubmit(form, coerceValues(form.fields, values))
  }

  return (
    <div className="mt-2 rounded-xl border border-teal-200 bg-teal-50/40 p-4">
      <div className="flex items-start gap-2.5">
        <ClipboardList
          className="mt-0.5 h-4 w-4 shrink-0 text-teal-600"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-800 leading-snug">
            {form.title}
          </p>
          {form.description && (
            <p className="mt-0.5 text-sm text-neutral-600">
              {form.description}
            </p>
          )}

          <div className="mt-3 space-y-3">
            {form.fields.map(field => {
              const fieldMissing =
                showErrors &&
                field.required &&
                isEmpty(field, values[field.name])
              return (
                <div key={field.name}>
                  {field.type !== "checkbox" && (
                    <label className="mb-1 block text-xs font-medium text-neutral-600">
                      {field.label}
                      {field.required && (
                        <span className="text-red-500"> *</span>
                      )}
                    </label>
                  )}
                  <FieldInput
                    field={field}
                    value={values[field.name]}
                    disabled={locked}
                    onChange={value =>
                      setValues(prev => ({ ...prev, [field.name]: value }))
                    }
                  />
                  {fieldMissing ? (
                    <p className="mt-0.5 text-xs text-red-600">
                      {field.label} is required
                    </p>
                  ) : (
                    field.helpText && (
                      <p className="mt-0.5 text-xs text-neutral-400">
                        {field.helpText}
                      </p>
                    )
                  )}
                </div>
              )
            })}
          </div>

          {form.submitted ? (
            <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-teal-700">
              <Check size={14} /> Submitted
            </p>
          ) : (
            <button
              type="button"
              disabled={locked}
              onClick={submit}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-teal-700 disabled:opacity-60"
            >
              {form.submitLabel || "Submit"}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default FormCard
